import {gql} from '@apollo/client';

import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  OldSavedSearchesQuery,
  OldSavedSearchesQueryVariables,
} from 'search/saved-searches/__generated/OldSavedSearchesQuery';
import {RecentSavedSearchesQuery} from 'search/saved-searches/__generated/RecentSavedSearchesQuery';
import {
  SavedSearchSettingsModalQuery,
  SavedSearchSettingsModalQueryVariables,
} from 'search/saved-searches/__generated/SavedSearchSettingsModalQuery';
import {SAVED_SEARCH_SETTINGS_MODAL_QUERY} from 'search/saved-searches/SavedSearchSettingsModal';

// This is just to maintain old behavior until we flip the feature flag
// and it contains the new behavior if the flag is set
export const OLD_SAVED_SEARCHES_QUERY = gql`
  query OldSavedSearchesQuery($section: SearchSection!) @skipAudit @engComponent(name: SIGHTGLASS) {
    savedSearchesForSection(section: $section) {
      ...SavedSearchGraphQL
    }
  }
  ${getFragmentWrapper('SavedSearch').fragment}
`;

export const SAVED_SEARCHES_QUERY = gql`
  query SavedSearchesQuery @skipAudit @engComponent(name: SIGHTGLASS) {
    savedSearches {
      ...SavedSearchGraphQL
    }
  }
  ${getFragmentWrapper('SavedSearch').fragment}
`;

export const SAVED_SEARCH_SHARING_WARNINGS_QUERY = gql`
  query SavedSearchSharingWarningsQuery($id: StrictString!, $collaboratorIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: SIGHTGLASS) {
    savedSearch(id: $id) {
      ... on SavedSearchNode {
        id
        unauthorizedCollaborators(collaboratorIds: $collaboratorIds) {
          apiId
          role
        }
      }
    }
  }
`;

// TODO(BNCH-50470): Switch to this query
export const RECENT_SAVED_SEARCHES_QUERY = gql`
  query RecentSavedSearchesQuery @skipAudit @engComponent(name: SIGHTGLASS) {
    recentSavedSearches {
      ...SavedSearchGraphQL
    }
  }
  ${getFragmentWrapper('SavedSearch').fragment}
`;

// This function serves as a helper for refetching saved searches so that we don't have to thread refetch
// props all over the place, and the loading of searches can live with the dropdown component where the
// data is actually used. But the refetching of all of the saved searches in the modal is passed through
// as a prop to ensure it's only called when an action takes place from the modal.
export async function refetchSavedSearchesForSection(section: SearchSection): Promise<void> {
  await doQuerySafe<OldSavedSearchesQuery, OldSavedSearchesQueryVariables>(
    {query: OLD_SAVED_SEARCHES_QUERY},
    {variables: {section}, fetchPolicy: 'network-only'}
  );
}

export async function refetchSavedSearchSettings(savedSearchId: string): Promise<void> {
  await doQuerySafe<SavedSearchSettingsModalQuery, SavedSearchSettingsModalQueryVariables>(
    {query: SAVED_SEARCH_SETTINGS_MODAL_QUERY},
    {variables: {savedSearchId}, fetchPolicy: 'network-only'}
  );
}

export async function refetchRecentSavedSearches(): Promise<void> {
  await doQuerySafe<RecentSavedSearchesQuery>(
    {query: RECENT_SAVED_SEARCHES_QUERY},
    {fetchPolicy: 'network-only'}
  );
}
